import React, { useEffect, useMemo, useContext } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import moment from 'moment'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AiSparkles } from '@/components/ai/AiSparkles'
import WidgetContext from '@/components/widget/widget.context'
import WidgetContainer from '@/components/widget/WidgetContainer'
import { Pill } from '@/components/pills/Pill'

import { formatTotalMinutes, withAiNameSuffix } from './widgetHelpers'

const dateTimeFormat = (v) => (v ? moment.utc(v).local().format('MM/DD/YYYY h:mm:ss A') : '—')
const percentFormat = (v) => `${(Number(v) * 100).toFixed(1)}%`
const secondsFormat = (v) => (v != null ? `${Math.abs(Number(v) * 1000).toFixed(0)}ms` : 'N/A')
export const UsersWithAllCallsWidget = () => {
  const { data, loading } = useSelector((state) => state.analytics)
  const { handleChangeCsvData } = useContext(WidgetContext)

  const rows = useMemo(() => {
    return data.callCountsByUser.map((user) => {
      return {
        ...user,
        name: `${user.first_name} ${user.last_name}`,
        isAiAgent: Boolean(user.ai_agent),
      }
    })
  }, [data])

  const columns = [
    {
      accessor: 'name',
      label: 'Name',
      isSearchable: true,
      sticky: true,
      format: (name, rowData) => (
        <div className="flex">
          {name}
          {rowData.isAiAgent && <AiSparkles />}
        </div>
      ),
    },
    { accessor: 'organization_name', label: 'Organization', isSearchable: true },
    { accessor: 'email', label: 'Email', isSearchable: true },
    {
      accessor: 'tags',
      label: 'Tags',
      isSearchable: true,
      format: (tags = []) => (
        <div className="tags-column-entry">
          {tags.map((tag) => (
            <Pill key={tag} small brand data-testid="tag">
              {tag}
            </Pill>
          ))}
        </div>
      ),
    },
    {
      accessor: 'call_count',
      label: 'Total Call Count',
      format: (v) => Number(v).toLocaleString(),
      headerAlignment: 'right',
    },
    {
      accessor: 'total_duration_seconds',
      label: 'Total Minutes Used',
      format: (value) => formatTotalMinutes(value),
      headerAlignment: 'right',
      description:
        'The total time this user spent on calls in the selected date range, in minutes.',
    },
    {
      accessor: 'copied_summary_count',
      label: 'Notes Copied',
      format: (v) => Number(v || 0).toLocaleString(),
      headerAlignment: 'right',
    },
    {
      accessor: 'last_call',
      label: 'Last Call',
      format: (v) => dateTimeFormat(v),
      headerAlignment: 'right',
      description: 'The most recent time this user made or received a call through the app.',
    },
    {
      accessor: 'last_login',
      label: 'Last Agent App Login',
      format: (v) => dateTimeFormat(v),
      headerAlignment: 'right',
      description: 'The last time this user logged into the app.',
    },
    {
      accessor: 'last_logout',
      label: 'Last Agent App Logout',
      format: (v) => dateTimeFormat(v),
      headerAlignment: 'right',
      description: 'The last time this user logged out of the app.',
    },
    {
      accessor: 'audio_bleeding',
      label: 'Audio Bleed',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'When the speaker’s voice is picked up by the microphone on the other side, causing the same words to be heard and transcribed on both sides of the call.',
    },
    {
      accessor: 'snr_me',
      label: 'Low Signal to Noise Ratio - Agent',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'How often callers had trouble hearing because of background noise. 0% means everything sounded clear.',
    },
    {
      accessor: 'snr_them',
      label: 'Low Signal to Noise Ratio - Customer',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'How often callers had trouble hearing because of background noise. 0% means everything sounded clear.',
    },
    {
      accessor: 'missing_audio_me',
      label: 'Missing Audio - Agent',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'The percentage of time during a call during which no sound was received, which could mean connection issues or silence.',
    },
    {
      accessor: 'missing_audio_them',
      label: 'Missing Audio - Customer',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'The percentage of time during a call during which no sound was received, which could mean connection issues or silence.',
    },
    {
      accessor: 'empty_me',
      label: 'Empty Call - Agent',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'The percentage of time during a call had sound when the agent was not talking, which often means silence or a bad connection.',
    },
    {
      accessor: 'empty_them',
      label: 'Empty Call - Customer',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'The percentage of time during a call when the customer was not speaking, which often means silence or a bad connection.',
    },
    {
      accessor: 'forbidden_count',
      label: 'Logged Out',
      format: (v) => (v ? Number(v).toLocaleString() : 'N/A'),
      headerAlignment: 'right',
      description:
        'The number of calls that were not captured due to the agent being logged out or disconnected from the server',
    },
    {
      accessor: 'average_latency',
      label: 'Average First Packet Latency',
      format: (v) => secondsFormat(v),
      headerAlignment: 'right',
      description:
        'How long it took for the call to start sending audio after connecting. Lower times mean quicker call starts.',
    },
    {
      accessor: 'frontend_version',
      label: 'Frontend App Version',
      format: (v) => v || '—',
      headerAlignment: 'right',
      description: 'The version of the app installed on the user’s device.',
    },
    {
      accessor: 'backend_version',
      label: 'Backend App Version',
      format: (v) => v || '—',
      headerAlignment: 'right',
      description: 'The version of the app’s server that handled the call.',
    },
    {
      accessor: 'high_cpu_usage_count',
      label: 'High CPU Alerts',
      format: (v) => (v ? Number(v).toLocaleString() : 'N/A'),
      headerAlignment: 'right',
      description:
        'Indicates whether the user’s device was running slowly during the call, which can affect app performance.',
    },
    {
      accessor: 'missing_bytes_me',
      label: 'Missing Bytes - Agent',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'The percentage of lost data during the call can sometimes cause audio to cut out.',
    },
    {
      accessor: 'missing_bytes_them',
      label: 'Missing Bytes - Customer',
      format: (v) => percentFormat(v),
      headerAlignment: 'right',
      description:
        'The percentage of lost data during the call can sometimes cause audio to cut out.',
    },
    {
      accessor: 'desktop_errors_count',
      label: 'Desktop Errors',
      format: (v) => (v ? Number(v).toLocaleString() : '0'),
      headerAlignment: 'right',
      description:
        'How many times something went wrong with the app on the user’s computer. 0 means no errors detected.',
    },
    {
      accessor: 'installation_type',
      label: 'Installation Type',
      format: (v) => v || '—',
      headerAlignment: 'right',
      description: 'The format of the installer used to install Balto.',
    },
    {
      accessor: 'manual_stop_count',
      label: 'Manual Stops',
      format: (v) => Number(v).toLocaleString(),
      headerAlignment: 'right',
      description: 'How many times the user manually stopped the call.',
    },
  ]

  useEffect(() => {
    const headersToExport = columns.map((header) => {
      if (header.accessor === 'tags') {
        return { ...header, format: (v) => v.join(', ') }
      }
      if (header.accessor === 'name') {
        return { ...header, format: undefined }
      }
      return header
    })
    const csvRows = rows.map((row) => ({
      ...row,
      name: withAiNameSuffix(row.name, row.isAiAgent),
    }))
    handleChangeCsvData({ headers: headersToExport, data: csvRows })
  }, [rows])

  return (
    <WidgetContainer
      autoHeight={!loading.usagePage && !isEmpty(rows)}
      loading={loading.usagePage}
      noData={isEmpty(rows)}
      withPagination
      table={
        <AdvancedTable
          index="email"
          defaultOrderBy="last_login"
          defaultOrder="desc"
          columns={columns}
          rows={rows}
          pagination
          enableUpdateRowsPerPage
          showDescription
        />
      }
      chart={<div />}
    />
  )
}
